import type { Subscription } from '@customerio/hydra-ui-api';
import { isEmptyString } from '@/utils/string';
import { BillingSystem } from '../types';
import type { StripeBillingPlan } from './stripe-plan';

/**
 * A Stripe subscription with its plan resolved to the shared `BillingPlan` shape and a
 * `hasCard` convenience flag, with the raw wire fields preserved side-by-side (mirroring
 * how the Ember `SubscriptionModel` exposed both its attributes and its getters).
 */
export type StripeSubscriptionBillingData = Subscription & {
  /** Discriminant identifying this as a Stripe subscription (vs Ordway). */
  billingSystem: typeof BillingSystem.Stripe;
  /**
   * The subscription's plan, resolved from the billing service's plans list by `plan_id`.
   * Undefined only if the plan is somehow absent from the list (see {@link stripeSubscriptionToBillingData}).
   */
  plan: StripeBillingPlan | undefined;
  /** Whether the customer has a card on file. Ports the Ember `SubscriptionModel#hasCard` getter. */
  hasCard: boolean;
};

/**
 * Transform a Stripe subscription wire model into the shape the billing service exposes:
 * the raw wire fields, plus the active plan resolved to a {@link StripeBillingPlan} and a
 * `hasCard` flag.
 *
 * The wire subscription carries only a `plan_id`, so the active plan is matched from the
 * already-transformed `plans`. The billing `getInfo` endpoint always includes the active
 * plan in its `plans` list, so the lookup resolves in practice; the result is still typed
 * as possibly-undefined to keep the transform honest.
 */
export function stripeSubscriptionToBillingData(
  subscription: Subscription,
  plans: StripeBillingPlan[],
): StripeSubscriptionBillingData {
  const plan = plans.find((candidate) => candidate.id === subscription.plan_id);

  return {
    ...subscription,
    billingSystem: BillingSystem.Stripe,
    plan,
    hasCard: !isEmptyString(subscription.stripe_customer?.card_type),
  };
}
